import React, { useEffect, useRef } from 'react';
import LazyImage from './LazyImage';
import ImageCarousel from './ImageCarousel';

const HeroSection: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      
      try {
        const { clientX, clientY } = e;
        const { width, height, left, top } = heroRef.current.getBoundingClientRect();
        
        const x = (clientX - left) / width;
        const y = (clientY - top) / height;
        
        heroRef.current.style.setProperty('--mouse-x', `${x}`);
        heroRef.current.style.setProperty('--mouse-y', `${y}`);
      } catch (error) {
        console.warn('Mouse move handler error:', error);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const streamingPlatforms = [
    {
      name: 'Spotify',
      logo: 'https://storage.googleapis.com/pr-newsroom-wp/1/2018/11/Spotify_Logo_RGB_White.png',
      url: 'https://open.spotify.com/track/7ylYVINgpV4J3PDPXdvkIZ?context=spotify:playlist:4AqzWYRRNMj8bGcNcLVRRt&si=r_GErx_rT_mwGc2wN-Pwww'
    },
    {
      name: 'Apple Music',
      logo: 'https://ik.imagekit.io/bckqiai0e/Apple_Music_logo.png?updatedAt=1747987126257',
      url: 'https://music.lnk.to/iWfXLK'
    },
    {
      name: 'Beatport',
      logo: 'https://ik.imagekit.io/bckqiai0e/Beatport-Black.png?updatedAt=1747986324505',
      url: 'https://www.beatport.com/artist/dj-landan-time/679285/releases?srsltid=AfmBOopF6n98M6YN1XYKAMYeeVr2ek8ljzEHUdLzgZYdQkBi9Xxyuqlz'
    },
    {
      name: 'Traxsource',
      logo: 'https://ik.imagekit.io/bckqiai0e/logo-standard.png?updatedAt=1747986324223',
      url: 'https://www.traxsource.com/artist/437005/dj-landan-time'
    }
  ];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn('Image failed to load:', e.currentTarget.src);
    e.currentTarget.style.display = 'none';
  };

  const carouselImages = [
    {
      src: 'https://ik.imagekit.io/bckqiai0e/dj%20landan%20time%20so%20close%20artwork%203000.png',
      alt: 'DJ Landan Time - So Close'
    },
    {
      src: 'https://ik.imagekit.io/bckqiai0e/DJ%20Landan%20Time%20-%20Rap,Disco,Rock%20&%20Roll%20-%201400%20jpeg.jpg?updatedAt=1751219651790',
      alt: 'DJ Landan Time - Rap, Disco, Rock & Roll'
    },
    {
      src: 'https://ik.imagekit.io/bckqiai0e/DJ%20Landan%20Time%20-Tell%20Me-%20Clubsoul%20Remix-3000.png',
      alt: 'DJ Landan Time - Tell Me Clubsoul Remix'
    },
    {
      src: 'https://ik.imagekit.io/bckqiai0e/Overdrive%20DJ%20Landan%20Time%203000.png',
      alt: 'DJ Landan Time - Overdrive'
    },
    {
      src: 'https://ik.imagekit.io/bckqiai0e/What%20You%20Want%20-%20DJ%20Landan%20Time%20-%203000.jpg',
      alt: 'DJ Landan Time - What You Want'
    },
    {
      src: 'https://ik.imagekit.io/bckqiai0e/LAYDOWN%20(1400%20x%201400%20px).jpg',
      alt: 'DJ Landan Time - Laydown'
    }
  ];

  return (
    <section 
      id="home"
      ref={heroRef}
      className="min-h-screen flex items-center justify-center relative overflow-hidden pt-24"
      style={{ 
        '--mouse-x': '0.5', 
        '--mouse-y': '0.5'
      } as React.CSSProperties}
    >
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('https://ik.imagekit.io/bckqiai0e/background-waves-2.png')] bg-cover bg-center opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-dark via-brand-purple/20 to-brand-dark"></div>
      </div>

      <div className="relative z-10 text-center p-8 max-w-4xl mx-auto">
        <div className="mb-12">
          <div className="max-w-2xl mx-auto">
            <ImageCarousel images={carouselImages} />
          </div>
        </div>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Multi-versed Music Producer and DJ</h1>
          <div className="h-40 md:h-52 mx-auto flex items-center justify-center">
            <LazyImage
              src="https://ik.imagekit.io/bckqiai0e/DJ%20LANDAN%20TIM%20E%20500-Photoroom.png?updatedAt=1747750291333"
              alt="DJ Landan Time - Electronic Music DJ UK"
              className="h-full object-contain"
              loading="eager"
              onError={handleImageError}
            />
          </div>
        </div>
        <div className="text-gray-300 max-w-2xl mx-auto mb-8">
          <p>
            DJ Landan Time is an independent UK-based music producer, song writer, DJ, and remixer whose career is rooted in decades of experience behind the decks and a deep passion for dance music culture. Known for his versatility, he produces across all styles of house music — from deep and soulful to tech, big room, and festival-driven sounds — while also drawing on hip-hop, pop, and cinematic influences.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
          <a
            href="https://ffm.bio/8y5padb"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-purple hover:bg-brand-light text-white font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-brand-purple/30 transition-all duration-300"
          >
            Listen to My Music
          </a>
          <a
            href="#connect"
            className="bg-transparent hover:bg-brand-purple/20 text-white border-2 border-brand-purple font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-brand-purple/30 transition-all duration-300"
            onClick={(e) => {
              e.preventDefault();
              const element = document.querySelector('#connect');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            Connect With Me
          </a>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-8 mb-12 px-4">
          {streamingPlatforms.map((platform) => (
            <a
              key={platform.name}
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-transform hover:scale-105"
              aria-label={`Listen to DJ Landan Time on ${platform.name}`}
            >
              <LazyImage
                src={platform.logo}
                alt={`DJ Landan Time on ${platform.name}`}
                className={`h-8 md:h-10 w-auto object-contain ${platform.name === 'Beatport' || platform.name === 'Traxsource' ? 'filter brightness-0 invert opacity-80 hover:opacity-100' : 'opacity-80 hover:opacity-100'}`}
                onError={handleImageError}
              />
            </a>
          ))}
        </div>
      </div>
      
      <div className="absolute bottom-10 left-0 right-0 flex justify-center">
        <div className="animate-bounce">
          <svg className="w-6 h-6 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;